// CSV import + template-download BUTTONS for the identity-mapping workspace
// (FD-099). "Download template" fetches the per-type CSV template and saves it;
// "Import CSV" reads a file and posts it to the import endpoint, which runs the
// SAME validation/write path as the manual picker. Results render with the same
// per-row bucket semantics (accepted / updated / rejected) and the shared
// consent MessageBar.
//
// The behaviour now lives in `useIdentityCsv`, because the campaign route
// carries these two actions in its `···` menu instead (a menu cannot own the
// hidden file input — see that file). This component is the BUTTON variant, and
// its only mount point is `Resources › Identities`, which has no `···` menu of
// its own.

import { Button, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { Download, Upload } from 'lucide-react';

import { useIdentityCsv } from './useIdentityCsv';

export interface IdentityMappingCsvControlsProps {
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  // Invoked after an import that wrote at least one row, so the parent can
  // refresh the mapping table (react-query invalidation).
  onImported: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  actions: { display: 'flex', columnGap: tokens.spacingHorizontalS, alignItems: 'center' },
});

export default function IdentityMappingCsvControls({
  migrationId,
  onImported,
}: IdentityMappingCsvControlsProps) {
  const styles = useStyles();
  const { busy, downloadTemplate, openFilePicker, fileInput, feedback } = useIdentityCsv({
    migrationId,
    onImported,
  });

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        <Button
          appearance="secondary"
          icon={<Download size={16} strokeWidth={1.5} />}
          disabled={busy !== null}
          onClick={downloadTemplate}
        >
          Download template
        </Button>
        <Button
          appearance="secondary"
          icon={<Upload size={16} strokeWidth={1.5} />}
          disabled={busy !== null}
          onClick={openFilePicker}
        >
          Import CSV
        </Button>
        {busy && (
          <Spinner size="tiny" label={busy === 'import' ? 'Importing…' : 'Preparing…'} />
        )}
        {fileInput}
      </div>
      {feedback}
    </div>
  );
}
